import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Building2, Users2, CheckCircle2, PauseCircle, Rocket, ChevronRight, Activity } from "lucide-react";

function Stat({ label, value, tone = "slate", icon: Icon, testid }) {
  const tones = {
    slate:   "border-slate-800 bg-slate-900 text-slate-100",
    emerald: "border-emerald-800 bg-emerald-950/40 text-emerald-100",
    amber:   "border-amber-800 bg-amber-950/40 text-amber-100",
    rose:    "border-rose-800 bg-rose-950/40 text-rose-100",
    sky:     "border-sky-800 bg-sky-950/40 text-sky-100",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
          <div className="text-2xl font-heading font-semibold mt-1 tabular-nums">{value ?? 0}</div>
        </div>
        {Icon && <Icon className="w-6 h-6 opacity-40" />}
      </div>
    </div>
  );
}

export default function PlatformDashboard() {
  const { api } = usePlatformAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load dashboard"));
  }, [api]);

  if (err)  return <div className="p-6 text-rose-400 text-sm" data-testid="platform-dashboard-error">{err}</div>;
  if (!data) return <div className="p-6 text-slate-500 text-sm" data-testid="platform-dashboard-loading">Loading platform dashboard…</div>;

  const t = data.totals;
  return (
    <div className="p-6 space-y-6" data-testid="platform-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Overview</div>
          <h1 className="text-2xl font-heading font-semibold" data-testid="platform-dashboard-title">Platform Dashboard</h1>
          <p className="text-xs text-slate-500 mt-1">Aggregate operational health across every organisation on Ayobell. No clinical or service-user information is shown here.</p>
        </div>
        <div className="text-[10px] text-slate-500">Generated {data.health?.generated_at?.slice(0,19).replace('T',' ')}</div>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="platform-kpis">
        <Stat label="Total Organisations" value={t.organisations} tone="sky"     icon={Building2} testid="kpi-total-orgs" />
        <Stat label="Active Customers"    value={t.active}        tone="emerald" icon={CheckCircle2} testid="kpi-active" />
        <Stat label="Trial Customers"     value={t.trial}         tone="amber"   icon={Rocket}       testid="kpi-trial" />
        <Stat label="Suspended Customers" value={t.suspended}     tone="rose"    icon={PauseCircle}  testid="kpi-suspended" />
        <Stat label="Total Sites"         value={t.sites}         tone="slate"   icon={Building2}    testid="kpi-sites" />
        <Stat label="Licensed Users"      value={t.licensed_users} tone="slate"  icon={Users2}       testid="kpi-users" />
        <Stat label="Platform Health"     value={data.health.backend === "ok" ? "OK" : "Degraded"} tone="emerald" icon={Activity} testid="kpi-health" />
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="plans-summary">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Subscription plans</div>
          <div className="text-[10px] text-slate-500">{data.plans.length} plans on file</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              <tr className="border-b border-slate-800">
                <th className="text-left py-2 pr-4">Plan</th>
                <th className="text-right py-2 pr-4">Seat limit</th>
                <th className="text-right py-2 pr-4">Price</th>
                <th className="text-right py-2 pr-4">Interval</th>
                <th className="text-right py-2 pr-4">Orgs on plan</th>
              </tr>
            </thead>
            <tbody>
              {data.plans.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/50" data-testid={`plan-row-${p.id}`}>
                  <td className="py-2 pr-4 font-medium">{p.name}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{p.seat_limit}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {p.price_pence === 0 ? "—" : `£${(p.price_pence / 100).toFixed(0)}`}
                  </td>
                  <td className="py-2 pr-4 text-right text-slate-400">{p.interval}</td>
                  <td className="py-2 pr-4 text-right tabular-nums text-emerald-300">{p.org_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Next steps</div>
            <div className="text-sm mt-1 text-slate-300">Onboard a new customer via the wizard — dry-run first, commit only after MFA and audited approval.</div>
          </div>
          <Link to="/platform-admin/onboarding" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="dashboard-goto-onboarding">
            Open onboarding wizard <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
